import { formatCount, parseMoneyToCents, sumCents, tt, type MoneyInput } from '@agos/shared'

/**
 * The shift handovers a franchise has on record.
 *
 * `manager-orders` owns the screen that *creates* one; this is the screen that
 * reviews them. The two share no arithmetic — creating a handover computes what is
 * owed from a live wallet, reviewing one checks whether what was recorded holds
 * together — so the logic stays separate rather than being forced into one module.
 *
 * Ported from legacy `pages/revenue-handovers.tsx`, which shows the stored numbers
 * and never compares them. FB-068 is what that misses.
 */

export interface HandoverReport {
  id?: number
  employeeId?: number
  employeeName?: string
  totalRevenue?: MoneyInput
  /** The online card figure the person entered. */
  cardPayAmount?: MoneyInput
  /** The terminal card figure the person entered. */
  cardAmount?: MoneyInput
  /** FB-068's new deduction. `null`/absent means the record predates it — not that nought was taken. */
  cardCourierAmount?: MoneyInput
  vouchersAmount?: MoneyInput
  fuelAmount?: MoneyInput
  purchase1Amount?: MoneyInput
  purchase2Amount?: MoneyInput
  payout1Amount?: MoneyInput
  payout1Note?: string | null
  payout2Amount?: MoneyInput
  payout2Note?: string | null
  cashToHandOver?: MoneyInput
  orderCount?: number
  /** Wallet snapshots — what the system says was taken, at the time. */
  cashTotal?: MoneyInput
  cardTerminalTotal?: MoneyInput
  cardCourierTotal?: MoneyInput
  cardOnlineTotal?: MoneyInput
  /**
   * QR payments taken at the door - §10A.390, owner O112. Both a part of the takings and a deduction the
   * server takes off the cash automatically, as it does the online card. **`null` or absent on a record
   * written before QR Pay was separated**, which never took it off - not a nought, see `qrPayTotalCentsOf`.
   */
  qrPayTotal?: MoneyInput
  createdAt?: string
  /**
   * When somebody marked the envelope as taken - BACKEND.md §10A.442, owner O143. `null` or absent on a record
   * nobody has marked; **handled is this being set**, and the three below only describe it.
   */
  handledAt?: string | null
  handledById?: number | null
  /** Null too when the person who marked it has since been deleted - the time survives, the name does not. */
  handledByName?: string | null
  handledNote?: string | null
}

/** Whether somebody has marked this record as handled - §9.791. */
export function isHandled(report: HandoverReport): boolean {
  return typeof report.handledAt === 'string' && report.handledAt !== ''
}

/**
 * What nobody has taken yet, over the records given - §9.791's *Nevybavené* line and the batch button.
 *
 * The cash is `auditHandover`'s `cashToHandOverCents`, the figure `handoverTotals` sums for the hero, so the line
 * and the headline cannot disagree about one record. The ids are every unhandled record given, **not one page of the
 * table** - the batch button marks what the filter shows.
 */
export function unhandledSummary(reports: HandoverReport[]): {
  count: number
  cashCents: number
  ids: number[]
} {
  const open = reports.filter((report) => !isHandled(report))
  return {
    count: open.length,
    cashCents: open.reduce((total, report) => total + auditHandover(report).cashToHandOverCents, 0),
    ids: open.flatMap((report) => (typeof report.id === 'number' ? [report.id] : [])),
  }
}

/**
 * What a batch mark says it did - §9.791.
 *
 * The server counts three things: the records it marked, the ones somebody else had marked first, and the ids it
 * did not find. Only the first is always worth saying; the other two are named only when they happened, because
 * *0 už predtým vybavil niekto iný* on every press reads as a warning about nothing. A count the server did not
 * send is read as nought rather than dropped, so an older server cannot make the sentence blank.
 */
export function batchOutcomeNote(outcome: { marked?: number; alreadyHandled?: number; missing?: number }): string {
  const marked = outcome.marked ?? 0
  const alreadyHandled = outcome.alreadyHandled ?? 0
  const missing = outcome.missing ?? 0
  const parts = [`${tt('Vybavené:')} ${formatCount(marked)}`]
  if (alreadyHandled > 0) parts.push(`${formatCount(alreadyHandled)} ${tt('už predtým vybavil niekto iný')}`)
  if (missing > 0) parts.push(`${formatCount(missing)} ${tt('sa nenašlo')}`)
  return parts.join(', ')
}

/**
 * The record's QR Pay figure, or `null` when it carries none - §9.741.
 *
 * **Null is a record from before the column existed, and it is not a nought.** `V85` added
 * `revenue_handovers.qr_pay_total` with no back-fill, because a handover recorded before then did not
 * separate QR at all: a nought would claim the day was checked for QR and none was found. An empty string is
 * read the same way - the server never sends one, and if anything did, nobody stated a figure.
 */
export function qrPayTotalCentsOf(report: HandoverReport): number | null {
  const stated = report.qrPayTotal
  if (stated === null || stated === undefined || stated === '') return null
  return parseMoneyToCents(stated)
}

/**
 * Everything the person said did not become cash in their pocket.
 *
 * **§9.461, FB-772 - `cardCourierAmount` was missing, and the screen called a correct handover
 * wrong.** The owner sent a driver's record: takings 418,40, a 268,35 courier card deducted, and
 * *"Záznam nesedí - tržba mínus odrátky dáva 370,30 €, uložené je 101,95 €"*. The 101,95 is right -
 * 418,40 less 47,35 CardPay, 268,35 courier card and 0,75 purchase - and this sum left the courier
 * card out, so *Celkom odrátky* read 48,10 instead of 316,45 and `arithmeticOff` fired on a record
 * that added up.
 *
 * **Invisible until §9.456**, which is the honest shape of it: the field was null on every stored row,
 * so omitting it cost nothing until something started filling it in. The server's
 * `HandoverReconciliation` has subtracted it all along - this side of the seam was the wrong one, and
 * a screen disagreeing with the server about whether money reconciles is worse than either answer.
 *
 * **§9.741 - and QR Pay, in the same change as the server's deduction (§10A.390).** The server now takes a
 * door QR payment off the cash automatically, so every record that took one would otherwise read *nesedí
 * súčet* by exactly that figure - FB-772's shape a second time. A record from before carries `null`, which
 * `sumCents` adds as nought: its cash never had QR off, and it holds no QR order to take off.
 */
export function deductionCents(report: HandoverReport): number {
  return sumCents(
    report.cardPayAmount,
    report.qrPayTotal,
    report.cardAmount,
    report.cardCourierAmount,
    report.vouchersAmount,
    report.fuelAmount,
    report.purchase1Amount,
    report.purchase2Amount,
    report.payout1Amount,
    report.payout2Amount,
  )
}

export interface HandoverAudit {
  totalRevenueCents: number
  deductionCents: number
  /** §9.741 - `qrPayTotalCentsOf`: `null` on a record from before QR Pay was separated. */
  qrPayTotalCents: number | null
  cashToHandOverCents: number
  /** What the record itself says the cash should be. */
  expectedCashCents: number
  /** The record does not add up. */
  arithmeticOff: boolean
  /** What the wallet says was taken by card, terminal + courier. */
  walletCardCents: number
  /** What the person entered as the terminal card figure. */
  enteredCardCents: number
  /** Entered minus wallet. Positive means they deducted more card than was taken. */
  cardDriftCents: number
  cardDrifts: boolean
  /**
   * Whether this record states card taken at the door — FB-068.
   *
   * False for every handover written before the column existed, which is the 363 in the dumps. That
   * is why `walletCardCents` above still means terminal **plus** courier for them: with no statement
   * of which convention was used, the combined figure is the only reading available.
   */
  doorStated: boolean
  /** What the register says was taken at the door. */
  doorWalletCents: number
  /** What the person entered for it — nought when the record does not state it. */
  doorEnteredCents: number
  doorDriftCents: number
  doorDrifts: boolean
  /**
   * The door takings look like they were typed into the *Karta* box - §9.452, FB-772.
   *
   * **The owner asked why a handover reported a 201,40 over-deduction**, and the record answers it:
   * a driver took **201,30 at the door**, the wallet's **terminal was 0,00**, and he entered
   * **201,40** as *Karta* while leaving *Karta kuriér* empty. So the card comparison reported the
   * whole 201,40 as over-deducted and the door comparison reported 201,30 as under-deducted -
   * **two halves of one mistake, read as two independent discrepancies**. His cash was out by
   * **ten cents**.
   *
   * When this is true the money is accounted for and in the wrong box, which is a different thing
   * to tell somebody than *you deducted 201,40 too much*.
   */
  doorCardInTheWrongBox: boolean
}

/** Anything under a cent is rounding, not a discrepancy. */
const TOLERANCE_CENTS = 0

/**
 * Whether a stored handover holds together, and whether it agrees with the wallet.
 *
 * Two separate questions, and only the first one legacy could have answered:
 *
 *   1. **Does the record add up?** Revenue minus the deductions should equal the
 *      cash handed over. Across all 363 handovers in the restored data it always
 *      does, so the form's own arithmetic is sound.
 *   2. **Does the card figure match what was actually taken by card?** In **103 of
 *      those 363** it does not. That is FB-068, and it is the one that costs money:
 *      a card figure entered too high means the person hands over less cash than
 *      they took, and too low means more.
 *
 * The record **used to have no field for courier card takings**, so a driver's card-at-the-door was
 * either folded into `cardAmount` or not deducted at all, and the 103 mismatches say it was not done
 * consistently. `card_courier_amount` exists now (`MIGRATION-STATUS` FB-068), and this compares like
 * with like when it is stated:
 *
 * - **Stated** — the entered terminal figure is checked against the register's terminal, and the
 *   entered door figure against the register's courier. Two comparisons, neither of them a guess.
 * - **Absent** — the historic case, and the 103. The entered `cardAmount` is checked against
 *   terminal **plus** courier, exactly as before, because that is the only reading available for a
 *   record whose convention nobody wrote down. Those stay flagged, which is right: the money really
 *   is unaccounted for and the audit is what says so.
 */
export function auditHandover(report: HandoverReport): HandoverAudit {
  const totalRevenueCents = parseMoneyToCents(report.totalRevenue)
  const deductions = deductionCents(report)
  const cashToHandOverCents = parseMoneyToCents(report.cashToHandOver)
  const expectedCashCents = totalRevenueCents - deductions

  // Stated only when the field carries a value. An empty string and `null` are both *not stated*;
  // `0` is a driver saying they took no card at the door, which is a claim and is treated as one.
  const doorStated = report.cardCourierAmount !== null && report.cardCourierAmount !== undefined
  const walletCardCents = doorStated
    ? parseMoneyToCents(report.cardTerminalTotal)
    : sumCents(report.cardTerminalTotal, report.cardCourierTotal)
  const enteredCardCents = parseMoneyToCents(report.cardAmount)
  const cardDriftCents = enteredCardCents - walletCardCents

  // The second comparison, and it only exists for a record that states the figure.
  const doorWalletCents = parseMoneyToCents(report.cardCourierTotal)
  const doorEnteredCents = doorStated ? parseMoneyToCents(report.cardCourierAmount) : 0
  const doorDriftCents = doorStated ? doorEnteredCents - doorWalletCents : 0

  /* §9.460, FB-772's second half - **a filing error is not a difference, and saying it is both was
   * the defect.** The owner sent a record whose message read *"Kasa zaznamenala na kartách 0,00 €"*
   * and, two clauses later, *"kasa zaznamenala u kuriéra 124,20 €"* - the same sentence contradicting
   * itself, because the card comparison is narrowed to the terminal while the explanation talks about
   * the courier. Their verdict on it: **"IT WORKED BEFORE"**, and they were right: before §9.456 sent
   * `cardCourierAmount`, `doorStated` was false, the comparison was the combined one, and 124,20
   * against 124,20 drifted by nothing.
   *
   * So when the two card boxes together match the two card takings together, nothing is missing.
   * `cardDrifts` and `doorDrifts` answer **false** and the screen says the one true thing: the money
   * is recorded, in the wrong box. That also takes the record out of `driftingCount`, which is what
   * put *2/3 uzávierok* and a `Rozdiel spolu` in front of the owner for money that was all there. */
  const wrongBox = doorCardInTheWrongBox(
    doorStated,
    doorEnteredCents,
    doorWalletCents,
    cardDriftCents,
  )

  return {
    totalRevenueCents,
    deductionCents: deductions,
    qrPayTotalCents: qrPayTotalCentsOf(report),
    cashToHandOverCents,
    expectedCashCents,
    arithmeticOff: Math.abs(expectedCashCents - cashToHandOverCents) > TOLERANCE_CENTS,
    walletCardCents,
    enteredCardCents,
    cardDriftCents,
    cardDrifts: !wrongBox && Math.abs(cardDriftCents) > TOLERANCE_CENTS,
    doorStated,
    doorWalletCents,
    doorEnteredCents,
    doorDriftCents,
    // The courier box is short by exactly what the card box is over, so it is the same one mistake.
    // Reporting it twice is what made one misfiling look like two discrepancies.
    doorDrifts: !wrongBox && doorStated && Math.abs(doorDriftCents) > TOLERANCE_CENTS,
    doorCardInTheWrongBox: wrongBox,
  }
}

/**
 * A typo's worth, and no more - §9.452.
 *
 * **Not a reconciliation tolerance.** `TOLERANCE_CENTS` above is nought because a cent is a
 * discrepancy; this is a different question: *is the card over-deduction the same figure as the
 * door takings?* The real record differed by **ten cents** on 201,30, which is somebody typing
 * 201,40 for 201,30 rather than a second mistake. A euro is generous enough for that and far too
 * small to match an unrelated amount.
 */
const SAME_FIGURE_CENTS = 100

/**
 * Whether the door takings were entered as card - §9.452, FB-772.
 *
 * **All four conditions, and each one rules out a different innocent case.** The record must state
 * the door figure (or the comparison is already the combined one and this cannot arise); the person
 * must have entered **nothing** there; the wallet must show door takings (or there is nothing to
 * misplace); and the card over-deduction must be **the same figure**. Anything looser would relabel
 * a genuine over-deduction as a filing error, which is the more comfortable reading and the wrong
 * one.
 */
function doorCardInTheWrongBox(
  doorStated: boolean,
  doorEnteredCents: number,
  doorWalletCents: number,
  cardDriftCents: number,
): boolean {
  if (!doorStated || doorEnteredCents !== 0 || doorWalletCents <= 0) return false
  return Math.abs(cardDriftCents - doorWalletCents) <= SAME_FIGURE_CENTS
}

export interface HandoverTotals {
  count: number
  revenueCents: number
  cashCents: number
  orderCount: number
  /** The wallet's own split, summed — what the register says was taken, by method. */
  walletCashCents: number
  walletTerminalCents: number
  walletCourierCents: number
  walletOnlineCents: number
  /**
   * §9.741 - the fifth part of the same takings, summed with a record from before as nought: such a record
   * holds no QR order, so its four parts already add up to its revenue.
   */
  walletQrPayCents: number
  /**
   * §9.767 - how many of the period's records carry no QR figure at all: recorded before QR Pay was separated, so
   * their QR money, if any, is inside their CardPay online figure. All of them is a period whose QR Pay was never
   * recorded, which `walletQrPayFigureCents` answers as `null`.
   */
  qrPayUnrecordedCount: number
  /** How many records disagree with the wallet about card takings. */
  driftingCount: number
  /** The net of those disagreements — what the discrepancies come to. */
  netDriftCents: number
  /**
   * What came out of the cash over the period — §9.252, the owner's item K.
   *
   * `cashToHandOver` is already net of all four, so the headline has always been real cash. These
   * are what it is net *of*, which no tile could answer.
   */
  payoutsCents: number
  purchasesCents: number
  fuelCents: number
  vouchersCents: number
  /** How many records carry a payout note, so an unexplained payout is visible. */
  annotatedPayouts: number
}

/**
 * The QR Pay figure the period's tile draws - FRONTEND.md §9.767, owner O129: drawn always, beside CardPay online.
 *
 * **`null` - *neevidované* - only when no record of the period carries a figure**, which is a period from before QR
 * Pay was separated: a nought there would claim it was checked. A period with some such records draws the sum of the
 * ones that carry one, and the tile says how many do not.
 */
export function walletQrPayFigureCents(totals: Pick<HandoverTotals, 'count' | 'walletQrPayCents' | 'qrPayUnrecordedCount'>): number | null {
  if (totals.count > 0 && totals.qrPayUnrecordedCount === totals.count) return null
  return totals.walletQrPayCents
}

export function handoverTotals(reports: HandoverReport[]): HandoverTotals {
  const audits = reports.map(auditHandover)
  const sum = (pick: (report: HandoverReport) => MoneyInput): number =>
    reports.reduce((total, report) => total + parseMoneyToCents(pick(report)), 0)

  return {
    count: reports.length,
    revenueCents: audits.reduce((total, audit) => total + audit.totalRevenueCents, 0),
    cashCents: audits.reduce((total, audit) => total + audit.cashToHandOverCents, 0),
    orderCount: reports.reduce((total, report) => total + (report.orderCount ?? 0), 0),
    walletCashCents: sum((report) => report.cashTotal),
    walletTerminalCents: sum((report) => report.cardTerminalTotal),
    walletCourierCents: sum((report) => report.cardCourierTotal),
    walletOnlineCents: sum((report) => report.cardOnlineTotal),
    walletQrPayCents: sum((report) => report.qrPayTotal),
    qrPayUnrecordedCount: reports.filter((report) => qrPayTotalCentsOf(report) === null).length,
    driftingCount: audits.filter((audit) => audit.cardDrifts).length,
    /* §9.588, FB-893 - **the total sums the records the count counted**, and it used to sum every
     * record. The two are printed as one sentence - *n/m uzávierok má odpočítanú sumu kariet inú [...]
     * Rozdiel spolu: x* - so a record excluded from `driftingCount` and included in `x` makes that
     * sentence name a figure that is not the records' own.
     *
     * The gap is precisely what §9.460 exists to remove: a misfiled courier card has `cardDrifts`
     * false because the money is all there in the wrong box, and its `cardDriftCents` is the size of
     * the misfiling. Measured on the owner's September 2026 before this line changed: the banner read
     * **377,95 €** over 14 records that net **52,35 €**, because handovers 563 and 564 - the two that
     * entry was written about - contributed 325,60 € to a total nobody had counted them in. Over the
     * whole year it read 103,86 € against a real -221,74 €, the opposite sign.
     *
     * `cardDriftCents` stays on the audit: the detail panel prints each record's own figure, and for a
     * misfiled record that figure is the whole of its explanation. What is wrong is summing it here. */
    netDriftCents: audits
      .filter((audit) => audit.cardDrifts)
      .reduce((total, audit) => total + audit.cardDriftCents, 0),
    // §9.252 — what came *out* of the cash over the period. `cashToHandOver` has always been net of
    // these, so the headline was already real cash; what nobody could see was how much of a week
    // went on wages and how much on a purchase without opening every record.
    //
    // CardPay and *Karta* are deliberately not among them: they are the same takings arriving by
    // another route, not money leaving the till, and counting them here would double against the
    // wallet split already on screen.
    payoutsCents: sum((report) => report.payout1Amount) + sum((report) => report.payout2Amount),
    purchasesCents:
      sum((report) => report.purchase1Amount) + sum((report) => report.purchase2Amount),
    fuelCents: sum((report) => report.fuelAmount),
    vouchersCents: sum((report) => report.vouchersAmount),
    /** How many of the period's payouts carry a note — an unexplained one is worth seeing. */
    annotatedPayouts: reports.filter((report) => payoutNotes(report).length > 0).length,
  }
}

/** The typed lines a handover collapsed into its two payout slots. */
export function payoutNotes(report: HandoverReport): string[] {
  return [report.payout1Note, report.payout2Note].filter(
    (note): note is string => typeof note === 'string' && note.trim() !== '',
  )
}

export function matchesSearch(report: HandoverReport, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [report.employeeName, ...payoutNotes(report)]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(needle))
}

/** §9.791 adds the two halves of *Vybavené* beside the drift filter. */
export type HandoverFilter = 'all' | 'drifting' | 'unhandled' | 'handled'

export function filterHandovers(
  reports: HandoverReport[],
  { filter, search }: { filter: HandoverFilter; search: string },
): HandoverReport[] {
  return reports.filter((report) => {
    if (filter === 'drifting' && !auditHandover(report).cardDrifts) return false
    if (filter === 'unhandled' && isHandled(report)) return false
    if (filter === 'handled' && !isHandled(report)) return false
    return matchesSearch(report, search)
  })
}

/** The label for a drift, phrased so its direction is unambiguous. */
export function driftDirection(cardDriftCents: number): string {
  if (cardDriftCents > 0) return tt('odpočítal viac kariet, než kasa zaznamenala')
  return tt('odpočítal menej kariet, než kasa zaznamenala')
}
